import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customersAPI } from '../lib/api';

// Mock data for fallback
const mockCustomers = [
  {
    id: '1',
    companyName: 'Tech Solutions Inc.',
    contactPerson: 'John Smith',
    email: 'john@techsolutions.com',
    phone: '+1-555-1001',
    outstandingBalance: 5500.00,
    creditLimit: 10000.00,
    creditStatus: 'Good',
    paymentTerms: 'Net 30',
    category: 'Technology'
  },
  {
    id: '2',
    companyName: 'Marketing Masters LLC',
    contactPerson: 'Sarah Johnson',
    email: 'sarah@marketingmasters.com',
    phone: '+1-555-1002',
    outstandingBalance: 3200.00,
    creditLimit: 15000.00,
    creditStatus: 'Excellent',
    paymentTerms: 'Net 15',
    category: 'Marketing'
  }
];

export const useCustomers = (companyId, startDate, endDate) => {
  return useQuery({
    queryKey: ['customers', companyId, startDate, endDate],
    queryFn: async () => {
      if (!companyId) {
        console.warn('No company ID provided, returning empty array');
        return [];
      }

      try {
        const response = await customersAPI.getAll(companyId, startDate, endDate);
        console.log('useCustomers hook - Raw API response:', response.data);
        
        // Handle the specific API response format: { Success: true, Data: [...] }
        let customers = [];
        if (response.data) {
          if (response.data.Success && Array.isArray(response.data.Data)) {
            customers = response.data.Data;
          } else if (Array.isArray(response.data)) {
            customers = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            customers = response.data.data;
          } else {
            console.warn('Unexpected API response format:', response.data);
            customers = [];
          }
        }
        
        // Map API fields (PascalCase) to component fields (camelCase)
        const mappedCustomers = customers.map(customer => ({
          id: customer.Id,
          customerName: customer.CustomerName || customer.CompanyName,
          contactPerson: customer.ContactPerson,
          email: customer.Email,
          phone: customer.Phone,
          address: customer.Address,
          creditLimit: customer.CreditLimit,
          paymentTerms: customer.PaymentTerms,
          category: customer.Category,
          taxId: customer.TaxId,
          sinceDate: customer.SinceDate,
          openingBalance: customer.OpeningBalance,
          invoiceCount: customer.InvoiceCount,
          invoiceAmount: customer.InvoiceAmount,
          paymentCount: customer.PaymentCount,
          paymentAmount: customer.PaymentAmount,
          endingBalance: customer.EndingBalance,
          balanceDirection: customer.BalanceDirection,
        }));
        
        console.log('useCustomers hook - Processed customers:', mappedCustomers);
        return mappedCustomers;
      } catch (error) {
        console.error('API error in useCustomers:', error);
        return [];
      }
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useCustomer = (id) => {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: async () => {
      try {
        const response = await customersAPI.getById(id);
        return response.data.data || response.data;
      } catch (error) {
        console.warn('API error, using mock data:', error.message);
        return mockCustomers.find(c => c.id === id) || null;
      }
    },
    enabled: !!id,
  });
};

export const useCreateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customerData) => {
      const payload = {
        CompanyName: customerData.companyName,
        ContactPerson: customerData.contactPerson,
        Email: customerData.email,
        Phone: customerData.phone,
        Address: customerData.address,
        TaxId: customerData.taxId,
        CreditLimit: customerData.creditLimit ? parseFloat(customerData.creditLimit) : 0,
        PaymentTerms: customerData.paymentTerms,
        Category: customerData.category,
        Notes: customerData.notes,
        CompanyId: customerData.companyId,
      };
      const response = await customersAPI.create(payload);
      return response.data.data || response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate customers list
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      
      // Add the new customer to the cache
      queryClient.setQueryData(['customer', data.id], data);
    },
  });
};

export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const payload = {
        CustomerName: data.companyName,
        ContactPerson: data.contactPerson,
        Email: data.email,
        Phone: data.phone,
        Address: data.address,
        TaxId: data.taxId,
        CreditLimit: data.creditLimit ? parseFloat(data.creditLimit) : 0,
        PaymentTerms: data.paymentTerms,
        Category: data.category,
        Notes: data.notes,
        CompanyId: data.companyId,
      };
      const response = await customersAPI.update(id, payload);
      return response.data.data || response.data;
    },
    onSuccess: (data, variables) => {
      // Update the customer in cache
      queryClient.setQueryData(['customer', variables.id], data);
      
      // Invalidate customers list
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      try {
        await customersAPI.delete(id);
        return { success: true };
      } catch (error) {
        console.warn('API error, using mock response:', error.message);
        return { success: true };
      }
    },
    onSuccess: (data, id) => {
      // Remove customer from cache
      queryClient.removeQueries({ queryKey: ['customer', id] });
      
      // Invalidate customers list
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};